import {
  Session,
  SupabaseClient,
  User as SupabaseUser,
} from '@supabase/supabase-js';

import { TABLES } from '../../../config/supabase';
import {
  AuthError,
  EmailCredentials,
  EmailSignupData,
  PasswordResetData,
  PasswordUpdateData,
} from '../../../types';
import { validatePassword } from '../../../utils/password-validation';

/**
 * Email authentication service
 * Handles email/password signup, signin, password reset, and email verification
 */
export class EmailAuthService {
  private supabase: SupabaseClient;

  constructor(supabaseClient: SupabaseClient) {
    this.supabase = supabaseClient;
  }

  // ============================================================================
  // Email Authentication
  // ============================================================================

  async signUpWithEmail(
    signupData: EmailSignupData,
    onProfileCreated?: (userId: string) => Promise<void>
  ): Promise<{
    success: boolean;
    needsVerification: boolean;
    user?: SupabaseUser;
    hasGoals?: boolean;
    error?: AuthError;
  }> {
    try {
      console.log('🚀 Starting email signup process for:', signupData.email);

      // Validate password strength
      const passwordValidation = validatePassword(signupData.password);

      if (!passwordValidation.isValid) {
        console.log('❌ Password validation failed');
        return {
          success: false,
          needsVerification: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Password does not meet requirements',
          },
        };
      }

      // Validate password confirmation
      if (signupData.password !== signupData.confirmPassword) {
        console.log('❌ Password confirmation mismatch');
        return {
          success: false,
          needsVerification: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Passwords do not match',
          },
        };
      }

      // Create user account
      const { data, error } = await this.supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
      });

      // Enhanced debug logging
      console.log('🔍 Supabase signup response:', {
        data: {
          user: data?.user
            ? {
                id: data.user.id,
                email: data.user.email,
                emailConfirmed: data.user.email_confirmed_at,
                createdAt: data.user.created_at,
              }
            : null,
          session: data?.session ? 'exists' : 'null',
        },
        error: error
          ? {
              message: error.message,
              status: error.status,
            }
          : null,
      });

      if (error) {
        console.log('❌ Supabase signup error:', error.message);
        return {
          success: false,
          needsVerification: false,
          error: {
            code: 'SIGNUP_ERROR',
            message: error.message,
          },
        };
      }

      if (!data.user) {
        console.log('❌ No user returned from signup');
        return {
          success: false,
          needsVerification: false,
          error: {
            code: 'SIGNUP_ERROR',
            message: 'Failed to create user account',
          },
        };
      }

      // Create user profile with onboarding status
      try {
        console.log('🔄 Creating user profile for:', data.user.id);
        const { error: profileError } = await this.supabase
          .from(TABLES.USER_PROFILES)
          .upsert(
            {
              id: data.user.id,
              status: 'onboarding',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'id' }
          );

        if (profileError) {
          console.log('⚠️ Error creating user profile:', profileError);
        } else {
          console.log('✅ User profile created successfully');
          if (onProfileCreated) {
            await onProfileCreated(data.user.id);
          }
        }
      } catch (profileError) {
        console.log('⚠️ Unexpected error creating user profile:', profileError);
      }

      const needsVerification = !data.session;
      console.log(
        `✅ Signup successful. Needs verification: ${needsVerification}`
      );

      return {
        success: true,
        needsVerification,
        user: data.user,
      };
    } catch (error) {
      console.log('❌ Unexpected error during signup:', error);
      return {
        success: false,
        needsVerification: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async signInWithEmail(
    credentials: EmailCredentials,
    onSessionCreated?: (session: Session) => Promise<void>
  ): Promise<{
    success: boolean;
    user?: SupabaseUser;
    session?: Session;
    error?: AuthError;
  }> {
    try {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(credentials.email)) {
        return {
          success: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid email format',
          },
        };
      }

      const { data, error } = await this.supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'SIGNIN_ERROR',
            message: error.message,
          },
        };
      }

      // Platform-specific session handling
      if (data.session && onSessionCreated) {
        await onSessionCreated(data.session);
      }

      return {
        success: true,
        user: data.user,
        session: data.session,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  // ============================================================================
  // Password Management
  // ============================================================================

  async requestPasswordReset(
    resetData: PasswordResetData,
    baseUrl: string
  ): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(resetData.email)) {
        return {
          success: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid email format',
          },
        };
      }

      const { error } = await this.supabase.auth.resetPasswordForEmail(
        resetData.email,
        {
          redirectTo: `${baseUrl}/auth/password-reset`,
        }
      );

      if (error) {
        return {
          success: false,
          error: {
            code: 'PASSWORD_RESET_ERROR',
            message: error.message,
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async updatePassword(passwordData: PasswordUpdateData): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      const { error } = await this.supabase.auth.updateUser({
        password: passwordData.password,
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'PASSWORD_UPDATE_ERROR',
            message: error.message,
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  // ============================================================================
  // Email Verification
  // ============================================================================

  async resendEmailVerification(email?: string): Promise<{
    success: boolean;
    error?: AuthError;
  }> {
    try {
      if (!email) {
        const {
          data: { user },
        } = await this.supabase.auth.getUser();
        email = user?.email;
      }

      if (!email) {
        return {
          success: false,
          error: {
            code: 'NO_EMAIL',
            message: 'No email address available for verification',
          },
        };
      }

      const { error } = await this.supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        return {
          success: false,
          error: {
            code: 'VERIFICATION_ERROR',
            message: error.message,
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  async isEmailVerified(): Promise<boolean> {
    try {
      const {
        data: { user },
      } = await this.supabase.auth.getUser();

      const isVerified = !!user?.email_confirmed_at;
      console.log('🔍 Email verification status:', {
        userId: user?.id,
        email: user?.email,
        isVerified,
        confirmedAt: user?.email_confirmed_at,
      });

      return isVerified;
    } catch (error) {
      console.log('❌ Error checking email verification:', error);
      return false;
    }
  }

  async checkEmailVerificationStatus(
    checkHasGoals: (userId: string) => Promise<boolean>
  ): Promise<{
    isVerified: boolean;
    hasGoals?: boolean;
    shouldRedirectToOnboarding?: boolean;
    error?: AuthError;
  }> {
    try {
      const {
        data: { user },
      } = await this.supabase.auth.getUser();

      if (!user) {
        return {
          isVerified: false,
          error: {
            code: 'NOT_AUTHENTICATED',
            message: 'User not authenticated',
          },
        };
      }

      const isVerified = !!user.email_confirmed_at;

      if (!isVerified) {
        return {
          isVerified: false,
          shouldRedirectToOnboarding: false,
        };
      }

      // Check if user has goals
      const hasGoals = await checkHasGoals(user.id);

      return {
        isVerified: true,
        hasGoals,
        shouldRedirectToOnboarding: !hasGoals,
      };
    } catch (error) {
      console.log('❌ Error checking email verification status:', error);
      return {
        isVerified: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }
}
